import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card } from '../components/Card';
import { AxisLensCard, AxisLensSignature } from '../components/AxisLensCard';
import { ClaimCorrectionControl } from '../components/ClaimCorrectionControl';
import { JobFitRadar } from '../components/JobFitRadar';
import { ScoreRing } from '../components/ScoreRing';
import { AlignmentEssayPanel, describeBullshitCategory } from '../components/resume/AlignmentEssayPanel';
import { DriveSaveButton } from '../components/resume/DriveSaveButton';
import { GapAddressPanel } from '../components/resume/GapAddressPanel';
import { JobResumePanel } from '../components/resume/JobResumePanel';
import { LensComparisonRadar } from '../components/LensComparisonRadar';
import {
  type CollapsibleSectionId,
  readCollapseState,
  toggleSection,
  writeCollapseState,
} from '../lib/collapsibleSection';
import { printCoverLetter } from '../lib/coverLetterToPrint';
import { distillDeepReadText } from '../lib/deepReadDistill';
import {
  type ApiError,
  type AxisEvidenceScoreRecord,
  type AxisLensReadRecord,
  type ClaimCorrectionRecord,
  type GapAddressCommitResult,
  type GapsListResult,
  type JobRecord,
  type JobScoreRecord,
  type RelocationScoreSnapshot,
  type ServedGap,
  type TailorResponse,
  claimCorrectionKey,
  getClaimCorrections,
  getJob,
  getJobGaps,
  getProfileAttributes,
  tailorJob,
  toggleJobRelocation,
  trackApplication,
} from '../lib/api';
import { applySourceLabel, displayLocationForCard, jobFitScore, staleListingNotice } from '../lib/jobBuckets';
import { formatJobDescription } from '../lib/jobDescriptionFormat';
import { orderLocationForUS } from '../lib/locationOrder';
import { dismissPreShaping, isPreShapingDismissed } from '../lib/preShapingPrompt';
import { buildPrintDocumentTitle } from '../lib/printDocumentTitle';
import { useAuth } from '../lib/useAuth';
import { useJobsInbox } from '../lib/useJobsInbox';
import styles from './Jobs.module.css';

type TailorPhase = 'idle' | 'tailoring' | 'done' | 'error' | 'entitlement_blocked';
type TrackPhase = 'idle' | 'tracking' | 'tracked' | 'error';

/**
 * Job detail screen. The header/match card reads the REAL job (via
 * useJobsInbox -> GET /jobs, found by id — the inbox list is already fetched
 * by the Jobs tab and this screen is usually reached FROM a card in that
 * list, so the primary fields paint instantly with zero extra fetch).
 *
 * LANE JOB-TRUST — the transparency surface: GET /jobs list rows carry only
 * the PRIMARY scenario's flattened score (fit_score/radar_axes directly on
 * the row); the per-scenario breakdown ("AU 62 · Stateside 78") and the
 * fuller radar/scenario-name context live ONLY on GET /jobs/:id's `scores[]`
 * (jobs.routes.ts:2140's own doc comment: "Latest score per scenario"). So
 * this screen ALWAYS fetches GET /jobs/:id for `scores[]` on mount — a
 * SEPARATE, additive effect from the existing inbox-first primary-field
 * resolution above (that resolution is untouched: it still prefers the
 * instant inbox row for company/title/location/etc; this second fetch exists
 * purely to get the per-scenario score rows the inbox list never carries).
 *
 * THIS BRIEF completes what the prior pass left as a documented demo: the
 * Tailor button now calls the REAL POST /jobs/:id/tailor (tailorJob,
 * jobs.routes.ts:419) and renders exactly what the server sent back — the
 * composed cover letter + CV brief (leadAchievements, gapTranslations), the
 * render-BLOCKING lint gate's own verdict when it fails (lintGate.blocks'
 * per-block diffs, never summarized away), and the grounding verdict's flags
 * when the server marked anything not-fully-grounded. Nothing here is a
 * hardcoded STRONG_POINTS/ADD_BULLETS list any more — before a tailor has
 * run, the "what Mira will add" section says exactly that (nothing to show
 * yet), never a placeholder claim about the user. The Track button posts to
 * the REAL POST /applications (trackApplication) so this becomes the actual
 * apply-terminus entry point the Track tab's board reads from.
 *
 * WAVE 1 / LANE C3 additions (QA #26/#25/#21 — human-in-the-loop LINKS only,
 * never an auto-apply; hard rule #1):
 *   #26 — a PROMINENT "Apply on {source}" link, derived from job.canonical_url
 *     (the SAME field JobCard's "Open source" link already uses) via
 *     applySourceLabel (jobBuckets.ts), opening the real listing in a new tab.
 *     This is a LINK, never a submit — the human still clicks through and
 *     applies on the source site themselves.
 *   #25 — the tailored cover letter was ALREADY returned by POST /jobs/:id/
 *     tailor and already rendered below (tailorResult.tailoring.coverLetter);
 *     this brief makes that parity with the resume UNMISTAKABLE — the button
 *     copy now says "Tailor my resume & cover letter" and the letter section
 *     is labeled as a real, ready deliverable with its own copy-to-clipboard
 *     action, not a quiet preview easy to miss under the CV brief above it.
 *   #21 — a multi-location job.location string ("London, UK; Ontario, CAN;
 *     Remote-Friendly, United States; San Francisco, CA") now leads with the
 *     US-relevant segment via orderLocationForUS (locationOrder.ts) — pure
 *     display reordering, never a re-score/re-bucket.
 */
export function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const [tailorPhase, setTailorPhase] = useState<TailorPhase>('idle');
  const [tailorResult, setTailorResult] = useState<TailorResponse | null>(null);
  const [tailorError, setTailorError] = useState<string | null>(null);
  // G1 (docs/CHANGE-CONTROL.md R184): the SERVED 402 body verbatim
  // (entitlement_required's tier/reason/hint) — never a client-authored
  // paraphrase of why tailoring is blocked (no-frontend-bandaids: the
  // backend explains itself, the FE just renders the explanation, same
  // discipline every other served-reason render in this file already follows
  // — see describeTailorError below for the pre-existing 503/400 cases).
  const [entitlementBlock, setEntitlementBlock] = useState<{ tier: string; reason: string; hint: string } | null>(
    null,
  );
  const [trackPhase, setTrackPhase] = useState<TrackPhase>('idle');
  const [trackError, setTrackError] = useState<string | null>(null);
  // JOB-WALK-2 fix #3 — the relocation toggle's own state. `null` = not yet
  // resolved (the profile fetch below hasn't returned) — distinct from
  // `false` (resolved: nothing declared) so the toggle never flashes an
  // incorrect default before the real value loads. 'saving' governs the
  // toggle's own in-flight write, independent of every other phase on this
  // screen. relocationError surfaces a failed write honestly (never a silent
  // no-op — same discipline trackError/tailorError already follow here).
  const [relocationDeclared, setRelocationDeclared] = useState<boolean | null>(null);
  const [relocationSaving, setRelocationSaving] = useState(false);
  const [relocationError, setRelocationError] = useState<string | null>(null);
  // RELOCATION-SCORE — the honest decomposition + copy. W19 (guided-walk
  // 2026-07-21/22) widened this from "toggle-POST-only" to ALSO seeded on
  // the initial GET /jobs/:id load (see the scoresRecord effect below) —
  // previously this stayed null until the user's FIRST toggle flip on THIS
  // job, so anyone who never touched the switch saw only the generic static
  // copy below it, never the precise "location isn't a factor here — this
  // role's location isn't resolved to a country yet" (or the home-country-
  // unknown twin) reason. A toggle flip still re-populates it with the
  // freshly-written state via the SAME POST /jobs/:id/relocation response
  // (RelocationToggleResult, a superset of RelocationScoreSnapshot) — one
  // state, two producers, same shape. Reset to null on a jobId change so a
  // stale decomposition from the PREVIOUS job can never bleed into this one
  // (see the jobId-keyed effect below) until the fresh GET resolves.
  const [relocationScore, setRelocationScore] = useState<RelocationScoreSnapshot | null>(null);
  // #25 — the cover letter's own copy-to-clipboard affordance (parity with
  // the resume side, which the user downloads/previews via the Track flow).
  // 'copied' is a purely cosmetic 1.6s confirmation flash, reset on any new
  // copy attempt; 'error' surfaces only when the Clipboard API itself throws
  // (e.g. no secure context) — never silently swallowed.
  const [copyPhase, setCopyPhase] = useState<'idle' | 'copied' | 'error'>('idle');
  // GAP 2 — the cover letter's Print/Save-as-PDF affordance (parity with the
  // résumé's own printResume flow below). 'blocked' means the popup was
  // blocked/couldn't open — reported honestly, never a silent no-op.
  const [printLetterNote, setPrintLetterNote] = useState<string | null>(null);
  // The applicant name line on the printed letter: the SAME real, served
  // displayName Home.tsx/Settings.tsx already render (Google OAuth's own
  // value on the user row) — never a client-invented name. null when the
  // user has no displayName on record (never a placeholder like "Candidate").
  const auth = useAuth();
  const inbox = useJobsInbox();

  const inboxJob = useMemo(() => inbox.state.jobs.find((j) => j.id === jobId), [inbox.state.jobs, jobId]);

  // WIRE-AUDIT fix #3 (docs/design/wire-audit-2026-07-05.md): a deep link /
  // refresh / share previously resolved ONLY from the in-memory inbox list and
  // rendered a false "Job not found" for a real job. The inbox row still wins
  // when present (instant nav from the Jobs list, zero extra fetch); GET
  // /jobs/:id is the fallback for a cold mount. A real 404 is the ONLY thing
  // rendered as missing — never "not loaded yet".
  const [fetchedJob, setFetchedJob] = useState<JobRecord | null>(null);
  const [fetchPhase, setFetchPhase] = useState<'idle' | 'loading' | 'missing' | 'error'>('idle');
  const job = inboxJob ?? (fetchedJob?.id === jobId ? fetchedJob : undefined);

  useEffect(() => {
    if (inbox.state.loading || inboxJob || !jobId || fetchedJob?.id === jobId) return;
    let cancelled = false;
    setFetchPhase('loading');
    getJob(jobId)
      .then((r) => {
        if (cancelled) return;
        setFetchedJob(r.job);
        setFetchPhase('idle');
      })
      .catch((err) => {
        if (cancelled) return;
        const apiErr = err as ApiError;
        setFetchPhase(apiErr?.status === 404 ? 'missing' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [inbox.state.loading, inboxJob, jobId, fetchedJob]);

  // LANE JOB-TRUST — the per-scenario score rows ("AU 62 · Stateside 78") and
  // the fuller radar context live ONLY on GET /jobs/:id (see this file's own
  // header). This is a SEPARATE effect from the primary-field resolution
  // above — it never gates the loading/missing states (a slow or failed
  // scores[] fetch must never block or 404 the rest of the page, which the
  // inbox row already renders fine without it); a failure here just leaves
  // the transparency cards in their honest "not available" state.
  const [scoresRecord, setScoresRecord] = useState<JobScoreRecord[] | null>(null);
  const [scoresJobId, setScoresJobId] = useState<string | null>(null);
  // AXIS-P1 — the lens's "how this role reads" read, riding the SAME GET
  // /jobs/:id fetch as scoresRecord above (zero extra network call). null
  // until this fetch resolves, and honestly null forever after if the
  // server had no ANTHROPIC_API_KEY configured — AxisLensCard renders both
  // cases identically (the honest "not available" state).
  const [axisRead, setAxisRead] = useState<AxisLensReadRecord | null>(null);
  // FIX-E part 2 (Lane L-H) — the per-axis grounded evidence read (userScores):
  // JobFitRadar's Layer-2 gold polygon + per-axis rows. Null = "not scored yet"
  // (the same honest state axisRead's own null covers). Set from the SAME getJob
  // fetch below, never a second request.
  const [userScores, setUserScores] = useState<AxisEvidenceScoreRecord[] | null>(null);
  // W21 (docs/WALK-FINDINGS-2026-07-21.md) — the honest staleness signal.
  // GET /jobs/:id already served asOf/staleMinutes/isStale as TOP-LEVEL
  // siblings of `job` (not nested inside it, unlike the inbox list's flat
  // JobRecord rows) — this screen never rendered them until now. Seeded from
  // the SAME getJob fetch below, never a second request; reset to honest
  // nulls on a fetch failure (mirrors scoresRecord/axisRead/userScores'
  // own fail-quiet reset in the catch branch).
  const [staleInfo, setStaleInfo] = useState<{
    asOf: string | null;
    staleMinutes: number | null;
    isStale: boolean | null;
  }>({ asOf: null, staleMinutes: null, isStale: null });

  useEffect(() => {
    if (!jobId || scoresJobId === jobId) return;
    let cancelled = false;
    getJob(jobId)
      .then((r) => {
        if (cancelled) return;
        setScoresRecord(r.scores);
        setAxisRead(r.axisRead);
        setUserScores(r.userScores);
        // W19 — seed the honest relocation-score decomposition from THIS
        // read-only GET (relocationScore's own doc comment above has the
        // full why). A toggle flip on this same job overwrites it with the
        // POST response afterward; nothing here contends with that.
        setRelocationScore(r.relocationScore);
        // W21 — seed the honest staleness signal from the SAME GET.
        setStaleInfo({ asOf: r.asOf, staleMinutes: r.staleMinutes, isStale: r.isStale });
        setScoresJobId(jobId);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail-quiet: the honest "no radar / no per-scenario breakdown yet"
        // states below already cover a null scoresRecord — no separate error
        // banner needed for a secondary, non-blocking read. relocationScore
        // stays null (its own jobId-keyed reset effect already put it there)
        // — the toggle panel's generic static copy covers this honestly.
        setScoresRecord(null);
        setAxisRead(null);
        setUserScores(null);
        setStaleInfo({ asOf: null, staleMinutes: null, isStale: null });
        setScoresJobId(jobId);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, scoresJobId]);

  // THE GAP-ADDRESS LOOP (docs/design/gap-address-loop-2026-07-05.md) — the
  // classified gap list (evidence vs situation) + each gap's addressed
  // state, served fresh from GET /jobs/:id/gaps. A SEPARATE, additive read
  // from scoresRecord above (that fetch still drives the radar/reasoning
  // card unchanged) — this one exists purely so a gap can render its
  // "Address this" affordance and its honest "addressed — see [x]" state
  // once closed. Re-fetched after every gap-address commit (a rescore may
  // have changed the gap list itself), never re-derived client-side.
  const [gapsResult, setGapsResult] = useState<GapsListResult | null>(null);
  const [openGap, setOpenGap] = useState<ServedGap | null>(null);
  const [showPreShaping, setShowPreShaping] = useState(false);
  // The "that's not right" ledger read (Phase 2 Lane B, D12) — every already-
  // corrected gap claim for this user, keyed by claimCorrectionKey(jobId,
  // flaggedText) so ClaimCorrectionControl below can render the terminal
  // strikethrough state immediately, no per-claim round trip. Fail-quiet
  // (empty map) mirrors refetchGaps's own fail-open discipline: a slow/failed
  // fetch never blocks the gaps panel, it just means corrections aren't
  // pre-marked yet this render.
  const [gapCorrections, setGapCorrections] = useState<Record<string, ClaimCorrectionRecord>>({});
  // Same ledger read, for the "strengths" bullets in the SAME why-this-fit
  // panel (the second of D12's five named surfaces this lane wires).
  const [strengthCorrections, setStrengthCorrections] = useState<
    Record<string, ClaimCorrectionRecord>
  >({});
  // Phase C/C-P1 — the THIRD named surface: the alignment essay panel's
  // flagged claims + whole-essay note. Same fail-quiet, keyed-map discipline
  // as gapCorrections/strengthCorrections above; passed straight through to
  // AlignmentEssayPanel (a separate component file) rather than duplicated
  // there, so this page stays the single owner of "which corrections has
  // this user already made" for the whole job-detail screen.
  const [essayCorrections, setEssayCorrections] = useState<
    Record<string, ClaimCorrectionRecord>
  >({});

  function refetchGaps(id: string) {
    getJobGaps(id)
      .then((r) => setGapsResult(r))
      .catch(() => {
        // Fail-quiet: the existing job.gaps plain-text fallback below still
        // renders something honest when this classified read isn't
        // available — never a blocking error over a secondary read.
        setGapsResult(null);
      });
  }

  useEffect(() => {
    if (!jobId) return;
    refetchGaps(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    getClaimCorrections('gap')
      .then((r) => setGapCorrections(r.corrections))
      .catch(() => setGapCorrections({}));
    getClaimCorrections('reasoning_bullet')
      .then((r) => setStrengthCorrections(r.corrections))
      .catch(() => setStrengthCorrections({}));
    getClaimCorrections('essay_line')
      .then((r) => setEssayCorrections(r.corrections))
      .catch(() => setEssayCorrections({}));
  }, []);

  // The pre-shaping "would you like to add anything beforehand?" prompt
  // (ruling #3) — shown once per job, per tab session, only when at least
  // one gap is still unaddressed. Dismissible (preShapingPrompt.ts,
  // sessionStorage-remembered — mirrors collapsibleSection.ts's own
  // discipline); never a nag loop.
  useEffect(() => {
    if (!jobId || !gapsResult) return;
    if (gapsResult.allAddressed || isPreShapingDismissed(jobId)) {
      setShowPreShaping(false);
      return;
    }
    setShowPreShaping(gapsResult.gaps.length > 0);
  }, [jobId, gapsResult]);

  function handleGapAddressed(jobIdForRefetch: string, result: GapAddressCommitResult) {
    setGapsResult({
      ok: true,
      gaps: result.gaps,
      nextUnaddressed: result.nextUnaddressed,
      allAddressed: result.gaps.every((g) => g.addressed),
    });
    // A committed gap may have changed the job's OWN fit/radar/reasoning
    // (the auto-rescore) — re-pull GET /jobs/:id so the "Why this fit" card
    // above reflects the fresh score too, not just the gap list. Sets
    // scoresJobId to the SAME id (not null) so this is a fresh value, never
    // re-triggering the OTHER effect's own fetch — this is a direct,
    // one-shot re-read, not a re-arm of that effect.
    if (result.rescored) {
      getJob(jobIdForRefetch)
        .then((r) => setScoresRecord(r.scores))
        .catch(() => {});
    }
  }

  // JOB-WALK-2 fix #3 — resolve the toggle's initial state from the user's
  // REAL stored declaration (attributes.declaredRelocation), never defaulted
  // to "on" or "off" before the read completes. This is a per-USER fact (not
  // per-job — the same declaration is read by ProtectedClaimsGate for every
  // tailor across every job), so it fetches ONCE per mount, independent of
  // jobId. Fail-quiet on a read error: the toggle simply stays unresolved
  // (relocationDeclared null renders as 'off' below, the honest safe default
  // — never assert a declaration the read couldn't confirm).
  useEffect(() => {
    let cancelled = false;
    getProfileAttributes()
      .then((r) => {
        if (cancelled) return;
        const declared = r.profile.attributes?.declaredRelocation;
        setRelocationDeclared(typeof declared === 'string' && declared.trim().length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setRelocationDeclared(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // RELOCATION-SCORE — a job-detail-scoped decomposition must never survive
  // a nav to a DIFFERENT job (job A's "AU 72 -> 60" reading bleeding into job
  // B's card would be a fabricated, cross-job number). relocationDeclared
  // itself stays per-USER (untouched, per its own effect above) — only the
  // SERVED decomposition resets here.
  useEffect(() => {
    setRelocationScore(null);
  }, [jobId]);

  // FIX 1 (owner's live walk, 2026-07-05 — "this should be the default
  // position so that everything's collapsed with little 'click me for
  // information'," sharpened to "make sure ALL those open items on the job
  // area are collapsed when you open the job detail"). Every informational
  // section on this page now defaults CLOSED — collapsibleSection.ts is the
  // single source of truth for that default (COLLAPSIBLE_SECTION_DEFAULT),
  // this hook just resolves it per-job and remembers a tap for the rest of
  // THIS tab session (sessionStorage, keyed by jobId — see that module's own
  // header for why session-scoped and per-job). Read once per jobId change
  // (a nav from job A to job B re-reads job B's own remembered state, never
  // carries job A's open sections forward); `jobId ?? ''` is a safe key for
  // this one read before the "no job" guard below even has a chance to
  // render — the real per-job storage key only ever gets exercised once a
  // real jobId is present.
  const [collapseState, setCollapseState] = useState(() => readCollapseState(jobId ?? ''));
  useEffect(() => {
    setCollapseState(readCollapseState(jobId ?? ''));
  }, [jobId]);

  /** Toggles one section, updates state, and best-effort persists it for the
   *  rest of this tab session — wired to <details>'s own onToggle (reading
   *  e.currentTarget.open) rather than a click handler, so this also stays
   *  correct if a section is ever opened/closed via keyboard (native
   *  <details> already supports Enter/Space on its <summary> with zero extra
   *  code) or the browser's own find-in-page auto-expand. */
  function handleToggleCollapse(section: CollapsibleSectionId, open: boolean) {
    setCollapseState((prev) => {
      if (prev[section] === open) return prev;
      const next = toggleSection(prev, section);
      writeCollapseState(jobId ?? '', next);
      return next;
    });
  }

  if (inbox.state.loading || fetchPhase === 'loading') {
    return <div className={styles.status}>Loading…</div>;
  }

  if (!job) {
    return (
      <div className={styles.empty}>
        <Link to="/jobs" className={styles.backButton}>
          <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
            <path
              d="M8 1L2 8l6 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Jobs
        </Link>
        {fetchPhase === 'error'
          ? 'Couldn’t load this job right now — head back to Jobs and try again.'
          : "I can't find this one — it may have been taken down, or the link's gone stale."}
      </div>
    );
  }

  const done = tailorPhase === 'done' && tailorResult != null;
  const realFit = jobFitScore(job);
  // RELOCATION-SCORE — the dial renders the SERVED effective score the
  // instant a toggle flip on THIS job has returned one (relocationScore is
  // reset to null on every jobId change above, so this can never carry a
  // PRIOR job's decomposition). Before the first flip on this job,
  // effectiveScore is null and the dial falls back to the base fit score
  // exactly as it always has — this is purely additive, never a change to
  // what renders before the user ever touches the toggle.
  const match = relocationScore?.effectiveScore ?? realFit;
  // #26 — the SAME canonical_url JobCard's "Open source" link already reads,
  // friendly-named by its host (jobBuckets.ts's applySourceLabel).
  const applySourceName = applySourceLabel(job.canonical_url);
  // W21 — the honest "may no longer be advertised" notice, seeded from the
  // GET /jobs/:id staleInfo state above (asOf/staleMinutes/isStale ride as
  // TOP-LEVEL siblings of `job` on this endpoint, never nested inside it —
  // see staleInfo's own doc comment). Same pure formatter JobCard.tsx uses.
  const staleNotice = staleListingNotice(staleInfo);
  // #21 — pure display reorder of a `;`-delimited multi-location string; a
  // single-location job.location passes through unchanged.
  // BOARD-TRUTH FIX (owner report: a "Nearby" card showed a foreign city as
  // its face) — prefers the SERVED resolved_location for home/relocate jobs,
  // same as JobCard.tsx (displayLocationForCard, jobBuckets.ts); every other
  // bucket renders the ordered raw string exactly as before this fix.
  const orderedLocation = displayLocationForCard(job, orderLocationForUS(job.location));
  // JOB-WALK-2 fix #1 — the "About this role" pretty-print fix. Pure
  // derivation of job.description into paragraph/bullet/heading blocks; see
  // jobDescriptionFormat.ts's own header for the full no-invention contract.
  const descriptionBlocks = formatJobDescription(job.description);
  const blocked = done && tailorResult.blocked === true;
  // M7 (2026-07-04) — gate ORDER: tailoring first, brevity advisory-only. The
  // server (jobs.routes.ts's tailorShouldBlock, TailorLintSeverity.ts) now sets
  // blocked:true ONLY when a non-brevity reason (an anti-AI-tell hit) survives;
  // a BREVITY-ONLY lintGate failure (over_budget / grade_too_high /
  // jargon_dense — the QA3 FK-artifact fix) still ships lintGate with its real
  // diffs, but blocked is false/absent. That is "advisory-with-diffs": the
  // tailored voice renders normally (the success row below still shows), and
  // these suggestions surface alongside it rather than instead of it.
  const advisoryLintGate = done && !blocked && tailorResult.lintGate && !tailorResult.lintGate.passed
    ? tailorResult.lintGate
    : null;
  const groundingFlags = done ? (tailorResult.grounding?.flags ?? []) : [];
  const notFullyGrounded = done && tailorResult.grounding != null && tailorResult.grounding.grounded === false;

  // LANE JOB-TRUST — the per-scenario chips read from the dedicated GET
  // /jobs/:id fetch's scores[] (scoresRecord), NEVER from job.radar_axes
  // directly: that flattened list-row field is only the PRIMARY scenario's
  // axes, so reading it here would silently hide every other scenario the
  // moment scoresRecord has loaded. `scores` is null while the secondary
  // fetch hasn't resolved yet (or failed) — every render below treats that
  // as "not available yet", never a fabricated empty/zeroed shape.
  const scores = scoresRecord;
  // AXIS-VIZ — "in flight" for the signature's honest "reading…" state is
  // EXACTLY the same fetch scoresJobId/scoresRecord already track (axisRead
  // rides the same GET /jobs/:id round trip, see the effect above) — a
  // genuinely in-flight fetch is `scoresJobId !== jobId` (the effect hasn't
  // resolved/failed for THIS job yet); once it resolves (success OR
  // fail-quiet catch), scoresJobId === jobId and this is false again,
  // regardless of whether axisRead itself came back null. Never a separate
  // invented "loading" flag.
  const axisReadLoading = Boolean(jobId) && scoresJobId !== jobId;

  async function handleTailor() {
    if (tailorPhase === 'tailoring' || !jobId) return;
    setTailorPhase('tailoring');
    setTailorError(null);
    setEntitlementBlock(null);
    try {
      const result = await tailorJob(jobId);
      setTailorResult(result);
      setTailorPhase('done');
    } catch (err) {
      // G1: a 402 from requireEntitlement('tailoring') is a DISTINCT phase
      // from a generic error — never rendered as "Try again" (the served
      // hint is "start your trial," not "retry the same failing request").
      const apiErr = err as ApiError;
      if (apiErr?.status === 402 && apiErr.body && typeof apiErr.body === 'object') {
        const body = apiErr.body as { error?: string; tier?: string; reason?: string; hint?: string };
        if (body.error === 'entitlement_required') {
          setTailorPhase('entitlement_blocked');
          setEntitlementBlock({
            tier: body.tier ?? 'free',
            reason: body.reason ?? 'no_subscription',
            hint: body.hint ?? 'start your trial',
          });
          return;
        }
      }
      setTailorPhase('error');
      setTailorError(describeTailorError(err));
    }
  }

  async function handleTrack() {
    if (trackPhase === 'tracking' || trackPhase === 'tracked' || !jobId) return;
    setTrackPhase('tracking');
    setTrackError(null);
    try {
      await trackApplication({
        jobId,
        coverLetterId: tailorResult?.coverLetterId,
      });
      setTrackPhase('tracked');
    } catch (err) {
      setTrackPhase('error');
      setTrackError(err instanceof Error ? err.message : 'Could not track this application — try again.');
    }
  }

  // RELOCATION-SCORE (supersedes JOB-WALK-2 fix #3's write-only version) —
  // flips the relocation declaration on/off AND gets the recomputed score
  // back in the SAME response (the owner's ruling: "an instant score update
  // based off of the toggle switch... currently doesn't exist and should").
  // ONE server call (toggleJobRelocation, POST /jobs/:id/relocation) now
  // does both the existing writing-flag write (the server composes the
  // identical sentence shape this handler used to build client-side, from
  // the job's own real location — never invented, never re-derived
  // client-side, matching locationOrder.ts's own precedent that country
  // inference belongs server-side) AND the deterministic, zero-Claude score
  // recompute. Optimistic-toggle-then-confirm: flips the switch immediately
  // for a responsive feel, then reconciles with the server's response; a
  // failed write rolls BOTH the toggle and any already-rendered effective
  // score back, and reports honestly (never a silent no-op).
  async function handleToggleRelocation() {
    if (relocationSaving || relocationDeclared === null || !jobId) return;
    const turningOn = !relocationDeclared;
    const previousDeclared = relocationDeclared;
    const previousScore = relocationScore;
    setRelocationSaving(true);
    setRelocationError(null);
    setRelocationDeclared(turningOn);
    try {
      const result = await toggleJobRelocation(jobId, turningOn);
      setRelocationScore(result);
    } catch (err) {
      setRelocationDeclared(previousDeclared);
      setRelocationScore(previousScore);
      setRelocationError(
        err instanceof Error ? err.message : 'Could not save that — try again.',
      );
    } finally {
      setRelocationSaving(false);
    }
  }

  // #25 — copies the REAL served letter text verbatim (tailorResult.tailoring
  // .coverLetter, the same string rendered below); nothing here rewrites or
  // reformats it. A Clipboard API failure (no secure context, permission
  // denied) surfaces as an honest 'error' phase rather than silently no-op'ing.
  async function handleCopyLetter() {
    if (!tailorResult) return;
    try {
      await navigator.clipboard.writeText(tailorResult.tailoring.coverLetter);
      setCopyPhase('copied');
      setTimeout(() => setCopyPhase('idle'), 1600);
    } catch {
      setCopyPhase('error');
    }
  }

  // GAP 2 — opens a print-only window with the REAL served letter text
  // verbatim (same string handleCopyLetter copies, same string rendered
  // below); a popup-blocked failure is reported inline, never a silent no-op
  // (same contract JobResumePanel's own print button follows). The popup's
  // title (and therefore the browser's default Save-as-PDF filename) follows
  // the house convention (printDocumentTitle.ts) — every segment traces to a
  // real served field (job.title/job.company here; auth.user?.displayName
  // for the name) and is silently omitted, never invented, when absent.
  function handlePrintLetter() {
    if (!tailorResult || !job) return;
    const documentTitle = buildPrintDocumentTitle({
      displayName: auth.user?.displayName,
      artifact: 'Cover Letter',
      roleTitle: job.title,
      company: job.company,
    });
    const ok = printCoverLetter(
      tailorResult.tailoring.coverLetter,
      auth.user?.displayName?.trim() || null,
      documentTitle,
    );
    setPrintLetterNote(
      ok ? null : 'Couldn’t open the print window — allow pop-ups for this site and try again.',
    );
  }

  return (
    <div className={styles.page}>
      <Link to="/jobs" className={styles.backButton}>
        <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
          <path
            d="M8 1L2 8l6 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Jobs
      </Link>

      <div className={styles.detailHeader}>
        <div className={styles.detailLogo}>{job.company.trim().charAt(0).toUpperCase() || '?'}</div>
        <div>
          <div className={styles.detailTitle}>{job.title}</div>
          {/* W19 (guided-walk 2026-07-21/22): "there's no actual location
              city country whatsoever" — a null/empty orderedLocation
              previously rendered as literal silence (the trailing template
              string collapsed to ''), so the header simply looked unfinished
              rather than honestly saying the posting itself never stated
              one. Matches this file's own thin-read idiom elsewhere (e.g.
              "Not yet scored" for a null match) — say the absence, never
              omit it. */}
          <div className={styles.detailMeta}>
            {job.company}
            {orderedLocation ? (
              ` · ${orderedLocation}`
            ) : (
              <span className={styles.detailMetaUnknown}> · Location not stated in this posting</span>
            )}
          </div>
        </div>
      </div>

      {/* #26 — the PROMINENT, human-clicked apply link (hard rule #1: a link,
          never a submit). Same job.canonical_url JobCard's "Open source" link
          already uses; friendly-named by host via applySourceLabel. */}
      <a
        href={job.canonical_url}
        target="_blank"
        rel="noreferrer"
        className={styles.applyLink}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Apply on {applySourceName}
      </a>
      {/* W21 (docs/WALK-FINDINGS-2026-07-21.md) — a plain warning beside the
          outbound link, never a blocked one: he can still choose to open it,
          he just isn't told a listing that hasn't been re-seen in weeks is
          confidently live. Same honest-absence italic idiom as
          .detailMetaUnknown above (matches R392's "Location not stated in
          this posting" voice/shape). */}
      {staleNotice ? <div className={styles.staleListingNotice}>{staleNotice}</div> : null}

      {/* JOB-WALK-2 fix #2 (Anthropic ANZ live walk, 2026-07-05 — "I'm not
          quite ready to tailor — I should be able to save the job to my
          tracking board"). trackApplication (POST /applications) already
          works standalone before any tailor has run: coverLetterId is
          optional (applications.routes.ts's TrackInput) and handleTrack
          below already reads tailorResult?.coverLetterId, which is simply
          undefined pre-tailor — so this is PLACEMENT + FRAMING, not a new
          write path. Creates the application at the real earliest board
          stage (APPLICATION_STATUSES' 'wishlist', displayed as "On my radar"
          per the Tracker's own vocabulary — applicationBoard.ts). Same
          trackPhase/handleTrack the post-tailor "Track this application"
          button already drives (one honest state machine for "is this job on
          my board", regardless of which button the user clicked) — tracking
          it here and tailoring it later both land the exact same row. */}
      {trackPhase === 'tracked' ? (
        <Link to="/track" className={styles.savedToBoardLink}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          On your board — view
        </Link>
      ) : (
        <div className={styles.saveToBoardRow}>
          <button
            type="button"
            className={styles.saveToBoardBtn}
            disabled={trackPhase === 'tracking'}
            onClick={handleTrack}
          >
            {trackPhase === 'tracking' ? 'Saving…' : 'Save to my board'}
          </button>
          <div className={styles.matchNote}>
            Not ready to tailor yet? I’ll keep this one on your radar until you are.
          </div>
          {trackPhase === 'error' && trackError ? (
            <div className={styles.tailorErrorRow} role="alert">
              {trackError}
            </div>
          ) : null}
        </div>
      )}

      <Card className={styles.matchCard}>
        {match === null ? (
          <div className={styles.unscoredDetail} title="Not yet scored">
            —
          </div>
        ) : (
          <ScoreRing pct={match} size={68} fontSize={26} />
        )}
        <div className={styles.matchBody}>
          <div className={styles.matchLabel}>{match === null ? 'Not yet scored' : 'Your current fit'}</div>
          <div className={styles.matchNote}>
            {match === null
              ? 'This job hasn’t been scored against your story yet.'
              : "This is the job's real score against your story today — tailoring below reframes your existing strengths for this role, it doesn't change this number."}
          </div>
          {/* RELOCATION-SCORE — the honest decomposition, visible the instant
              the toggle below has produced one for THIS job. Only renders
              once relocationScore.locationIsFactor is true (same-country /
              unknown-location toggles report modifier: 0 with a "not a
              factor" reason — nothing to decompose, so nothing extra
              renders here, matching the base card's own copy above). Base +
              modifier are named SEPARATELY, never collapsed into a single
              mystery-moved number. */}
          {relocationScore?.locationIsFactor ? (
            <div className={styles.matchNote} style={{ marginTop: 6 }}>
              Base story fit {relocationScore.baseScore ?? '—'}
              {relocationScore.modifier !== 0
                ? ` ${relocationScore.modifier > 0 ? '+' : ''}${relocationScore.modifier} location = ${relocationScore.effectiveScore}`
                : ' — location adjustment lifted, no drag applied'}
              .
            </div>
          ) : null}
          {/* AXIS-VIZ (owner's ruling on R299: "it would be good to show a
              visual indicator of what's being derived — like the match score
              for a position"). The lens's read used to live ONLY inside the
              collapsed "How this role reads" card below, invisible until a
              tap. This is the always-visible compact signature, placed right
              beside the score ring at the same glance-level prominence the
              match score itself gets — never a full re-render of the weight
              table (that stays exclusively in AxisLensCard, further down,
              still collapsed by default). PURE PRESENTATION: axisRead is
              served verbatim from the SAME GET /jobs/:id fetch scoresRecord
              already rides (see that effect's own comment) — this component
              computes nothing, it only renders what the server sent, or the
              honest "reading…"/nothing states while that read is null. */}
          <AxisLensSignature read={axisRead} isLoading={axisReadLoading} />
        </div>
      </Card>

      {/* RELOCATION-SCORE (supersedes JOB-WALK-2 fix #3's write-only version;
          Anthropic ANZ live walk, 2026-07-05 — "a toggle switch on if I move
          to Australia or country of origin of the role because that will
          impact the metrics — which I'm assuming are actually working,
          correct?" — followed by the owner's later, explicit build ruling:
          "They also need an instant score update based off of the toggle
          switch... Currently doesn't exist and should."). THE PROMISE IS NOW
          DUE: POST /jobs/:id/relocation (toggleJobRelocation, jobs.routes.ts)
          returns the recomputed effective score in the SAME response that
          saves the toggle — one round trip, the dial above updates
          instantly. The modifier is a DETERMINISTIC, code-only layer around
          the scorer's own stored fit_score (lib/relocationModifier.ts) —
          zero Claude calls on toggle, the base story-fit score itself is
          NEVER edited. relocationDeclared === null (the profile read hasn't
          resolved) renders the disabled state — never a guessed on/off.

          FIX 1 (2026-07-05, sharpened): the toggle CONTROL itself — the
          label, the honest on/off status line right beside it, and the
          switch — is an ACTION control per the brief's own list ("the
          relocation toggle switch" stays visible), so it renders exactly as
          before, never behind a collapse. Only the LONGER explainer
          paragraph below it is informational prose, not a control — that's
          what collapses, reusing the SAME <details>/<summary
          className={styles.sectionTitle}> idiom "Why this fit"/"About this
          role" already establish. relocationError always renders regardless
          of collapse state — a failed write is never hidden behind a tap. */}
      <Card className={styles.sectionCard}>
        <div className={styles.relocationToggleRow}>
          <div>
            <div className={styles.matchLabel}>
              {orderedLocation ? `Considering ${orderedLocation}?` : 'Considering relocating?'}
            </div>
            <div className={styles.matchNote}>
              {relocationDeclared
                ? 'On — I can speak to relocation in your cover letter and essay for this role, and the location drag on your fit score above is lifted.'
                : "Off — I won't mention relocation in what I write for you, and your fit score above honestly reflects the location gap when there is one."}
            </div>
            {/* The served, one-line reason from the SAME response that saved
                the toggle — rendered verbatim (no-frontend-bandaids: this
                app never composes its own explanation from raw numbers).
                Only shown once a flip on THIS job has produced one. */}
            {relocationScore ? (
              <div className={styles.matchNote} style={{ marginTop: 4 }}>
                {relocationScore.copy}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={relocationDeclared === true}
            disabled={relocationDeclared === null || relocationSaving}
            className={`${styles.toggleSwitch} ${relocationDeclared ? styles.toggleSwitchOn : ''}`}
            onClick={handleToggleRelocation}
          >
            <span className={styles.toggleKnob} />
          </button>
        </div>
        <details
          open={collapseState['relocation-explainer']}
          onToggle={(e) => handleToggleCollapse('relocation-explainer', e.currentTarget.open)}
          style={{ marginTop: 8 }}
        >
          <summary className={styles.sectionTitle} style={{ cursor: 'pointer', marginBottom: 0 }}>
            What this does
          </summary>
          <div className={styles.matchNote} style={{ marginTop: 8 }}>
            This does two things now: it lets me speak to relocation in what I write for you, AND it
            updates your fit score above instantly — on lifts the location drag, off applies it
            honestly when this role's country differs from yours. Your base story fit (how well your
            experience matches what they're asking for) never changes; only the separate location
            adjustment moves.
          </div>
        </details>
        {relocationError ? (
          <div className={styles.tailorErrorRow} role="alert" style={{ marginTop: 8 }}>
            {relocationError}
          </div>
        ) : null}
      </Card>

      {/* WIRE-AUDIT fix #2 (Jason's hard requirement: "the reasoning behind
          the current fit be explained" — collapsible ok). The scorer has
          ALWAYS produced + served reasoning/strengths/gaps on the job row
          (JobScoringService → job_scores → the GET selects); this card
          finally renders them. Served text verbatim; when a scored job has
          no stored reasoning, say exactly that — never a fabricated
          explanation.

          FIX 1 (2026-07-05, sharpened priority): this is the "scoring
          explainer" the collapse-everything push names explicitly — now
          CLOSED by default (collapsibleSection.ts's 'why-this-fit'), one tap
          to expand, session-remembered per job. Same native <details>/
          <summary className={styles.sectionTitle}> idiom this card already
          used when it defaulted open — only the default + the controlling
          `open` prop changed, nothing about the mechanism itself. */}
      {match !== null ? (
        <Card className={styles.sectionCard}>
          <details
            open={collapseState['why-this-fit']}
            onToggle={(e) => handleToggleCollapse('why-this-fit', e.currentTarget.open)}
          >
            <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
              Why this fit
            </summary>
            {job.reasoning || (job.strengths?.length ?? 0) > 0 || (job.gaps?.length ?? 0) > 0 ? (
              <>
                {job.reasoning
                  ? (() => {
                      // D15 (Class 7) — "the deep read isn't readable by
                      // humans... distilled into 3-4 meaningful bullet
                      // points." Mechanical sentence-split, not a
                      // re-summarization (see deepReadDistill.ts's header) —
                      // the source stays the ground truth, always reachable
                      // via the expandable below when there's more than the
                      // capped bullets show.
                      const distilled = distillDeepReadText(job.reasoning);
                      return (
                        <>
                          {distilled.bullets.length > 0 ? (
                            <div className={styles.bulletList} style={{ marginTop: 8 }}>
                              {distilled.bullets.map((sentence, i) => (
                                <div key={`${i}-${sentence.slice(0, 24)}`} className={styles.bulletRow}>
                                  <div className={`${styles.bulletDot} ${styles.bulletDotAdd}`}>•</div>
                                  <div className={styles.bulletText}>{sentence}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className={styles.matchNote} style={{ marginTop: 8 }}>
                              {distilled.fullText}
                            </div>
                          )}
                          {distilled.hasMore ? (
                            <details style={{ marginTop: 6 }}>
                              <summary className={styles.matchNote} style={{ cursor: 'pointer' }}>
                                Read the full reasoning
                              </summary>
                              <div className={styles.matchNote} style={{ marginTop: 6 }}>
                                {distilled.fullText}
                              </div>
                            </details>
                          ) : null}
                        </>
                      );
                    })()
                  : null}
                {job.strengths && job.strengths.length > 0 ? (
                  <div className={styles.bulletList} style={{ marginTop: 10 }}>
                    {job.strengths.map((text) => (
                      <div key={text} className={styles.bulletRow}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                          style={{ marginTop: 1, flexShrink: 0 }}
                        >
                          <path
                            d="M5 13l4 4L19 7"
                            stroke="var(--tl-good)"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className={styles.bulletText}>
                          {text}
                          {jobId ? (
                            <ClaimCorrectionControl
                              sourceType="reasoning_bullet"
                              sourceId={jobId}
                              flaggedText={text}
                              existingCorrection={
                                strengthCorrections[claimCorrectionKey(jobId, text)]
                              }
                              onCorrected={(c) =>
                                setStrengthCorrections((prev) => ({
                                  ...prev,
                                  [claimCorrectionKey(jobId, text)]: c,
                                }))
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {/* THE GAP-ADDRESS LOOP — each gap now carries its server-
                    classified class (evidence | situation) and an "Address
                    this" affordance opening a scoped Mira conversation
                    (docs/design/gap-address-loop-2026-07-05.md). Prefers the
                    classified gapsResult once it resolves; falls back to the
                    plain job.gaps text (no class, no affordance) so a slow
                    or failed classified fetch never hides the gaps
                    themselves — it just means the "Address this" button
                    isn't available yet. An ADDRESSED gap renders its own
                    honest resolved state instead of the button, so a closed
                    gap never resurfaces blind. */}
                {gapsResult && gapsResult.gaps.length > 0 ? (
                  <div className={styles.bulletList} style={{ marginTop: 10 }}>
                    {gapsResult.gaps.map((g) => (
                      <div key={g.id} className={styles.bulletRow}>
                        <div className={`${styles.bulletDot} ${styles.bulletDotAdd}`}>
                          {g.addressed ? '✓' : '–'}
                        </div>
                        <div className={styles.bulletText}>
                          {g.text}
                          {g.addressed ? (
                            <div className={styles.matchNote} style={{ marginTop: 2 }}>
                              Addressed —{' '}
                              {g.resolution?.status === 'evidence_committed'
                                ? "landed in your corpus"
                                : g.resolution?.status === 'situation_declared'
                                  ? 'you told Meira where things stand'
                                  : 'dismissed for now'}
                              .
                            </div>
                          ) : (
                            <button
                              type="button"
                              className={styles.copyLetterBtn}
                              style={{ marginTop: 4 }}
                              onClick={() => setOpenGap(g)}
                            >
                              I'd like to address that
                            </button>
                          )}
                          {/* D12 — the ASK-half (Address this) and the
                              DISPUTE-half (that's not right) are siblings on
                              the SAME claim: a gap can be both "not something
                              I've done" (address it) and "not even a real
                              gap for me" (dispute it) — never mutually
                              exclusive, so both render. */}
                          {jobId ? (
                            <ClaimCorrectionControl
                              sourceType="gap"
                              sourceId={jobId}
                              flaggedText={g.text}
                              existingCorrection={gapCorrections[claimCorrectionKey(jobId, g.text)]}
                              onCorrected={(c) =>
                                setGapCorrections((prev) => ({
                                  ...prev,
                                  [claimCorrectionKey(jobId, g.text)]: c,
                                }))
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : job.gaps && job.gaps.length > 0 ? (
                  <div className={styles.bulletList} style={{ marginTop: 10 }}>
                    {job.gaps.map((text) => (
                      <div key={text} className={styles.bulletRow}>
                        <div className={`${styles.bulletDot} ${styles.bulletDotAdd}`}>–</div>
                        <div className={styles.bulletText}>
                          {text}
                          {jobId ? (
                            <ClaimCorrectionControl
                              sourceType="gap"
                              sourceId={jobId}
                              flaggedText={text}
                              existingCorrection={gapCorrections[claimCorrectionKey(jobId, text)]}
                              onCorrected={(c) =>
                                setGapCorrections((prev) => ({
                                  ...prev,
                                  [claimCorrectionKey(jobId, text)]: c,
                                }))
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div className={styles.matchNote} style={{ marginTop: 8 }}>
                I scored this one, but I didn't write down my reasoning at the time — sorry about
                that.
              </div>
            )}

            {/* D24 (2026-07-08 live-test, supersedes D11's original framing)
                — "how we feel a job is in ONE layer, and how my skill set as
                described so far fits to match that shape." Replaces the old
                candidate-only 6-axis radar (which only ever plotted
                radarAxes — one shape, self-vs-self, not job-vs-candidate)
                with JobFitRadar: layer 1 = the job's own cornerstone weights
                (AxisLensReadRecord.weights, the SAME read AxisLensCard's
                composed bar below already renders), layer 2 = candidate
                evidence-so-far against that same axis set (honestly absent
                at ship time — see jobFitAxisPath.ts's header; never a
                fabricated overlay). axisReadLoading mirrors AxisLensSignature's
                own loading gate a few lines above — the SAME fetch this
                page already tracks, not a second one. */}
            <div style={{ marginTop: 14 }}>
              {axisReadLoading ? (
                <div className={styles.matchNote}>Loading the fit breakdown…</div>
              ) : (
                <JobFitRadar axisRead={axisRead} evidenceScores={userScores} />
              )}
            </div>

            {/* LANE JOB-TRUST — per-scenario chips: honest, only when this
                job has actually been scored under more than one search
                scenario (the route's own comment: "AU 62 · Stateside 78").
                A single-scenario job renders nothing extra here — the card
                above already IS that one scenario's reasoning. Every chip
                shows the served fitScore, or the honest fitDetermination
                word when it's null (undetermined/unscored) — never a
                fabricated 0. */}
            {scores !== null && scores.length > 1 ? (
              <div className={styles.scenarioChipRow} style={{ marginTop: 14 }}>
                {scores.map((s) => (
                  <div key={s.id} className={styles.scenarioChip}>
                    <span className={styles.scenarioChipName}>{s.scenarioName}</span>
                    <span className={styles.scenarioChipScore}>
                      {s.fitScore === null ? scenarioScoreLabel(s.fitDetermination) : Math.round(s.fitScore)}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </details>
        </Card>
      ) : null}

      {/* AXIS-P1 (docs/design/axis-lens-plan-2026-07-06.md) — "How this role
          reads": the lens's JD-weight read, served below the score card per
          the mission's P1 scope. PURE PRESENTATION (AxisLensCard renders
          exactly what the server sent — no-frontend-bandaids law); collapsed
          by default, same native <details>/<summary className={styles.
          sectionTitle}> idiom every other informational card on this page
          uses. Renders the SAME served weights JobFitRadar's layer 1 plots
          above (a bar view vs. a radar view of one read, never two
          derivations) — see AxisLensService.ts's own reconciliation note on
          why this concept never merges with job.radar_axes/JobScoreRecord's
          candidate-fit rubric (a genuinely different served field, no longer
          rendered on this page as of D24 — JobFitRadar's layer 2 is the
          candidate-evidence read now). This card's own copy never says bare
          "axes". Renders even when axisRead is still null
          (server had no key configured, or the scores fetch hasn't resolved
          yet) — AxisLensCard's own honest "not available" state covers both,
          never a fabricated placeholder read.

          AXIS-VIZ — this collapsed card is now the EXPANDED form only; the
          always-visible summary (AxisLensSignature, top 3 bars + register +
          honest caveat) already rendered above, right beside the score ring.
          This full card still carries the complete weight list + the
          coverage percentage prose — the receipts a curious user can dig
          into, never duplicated in the compact signature above. */}
      <Card className={styles.sectionCard}>
        <details
          open={collapseState['how-this-role-reads']}
          onToggle={(e) => handleToggleCollapse('how-this-role-reads', e.currentTarget.open)}
        >
          <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
            How this role reads
          </summary>
          <div style={{ marginTop: 8 }}>
            <AxisLensCard read={axisRead} />
          </div>
        </details>
      </Card>

      {/* LANE JOB-TRUST — "About this role": the biggest trust hole per
          Jason's own framing (you can't read the job you're scoring). Served
          job.description verbatim, whichever source it resolved from
          (loadJobForUser: the legacy jobs row directly, or the lake's
          job_listing_details.description) — this app never re-writes or
          summarizes it. Honest empty state when the source never carried a
          description (a thin paste-import, or an extraction that hasn't
          captured body text yet) — never a fabricated placeholder job blurb.

          FIX 1 (2026-07-05, sharpened priority): "About this role" is named
          explicitly in the collapse-everything push — now CLOSED by default
          (collapsibleSection.ts's 'about-this-role'), one tap to expand,
          session-remembered per job. Same native <details>/<summary
          className={styles.sectionTitle}> idiom this card already used when
          it defaulted open — only the default + the controlling `open` prop
          changed.

          JOB-WALK-2 fix #1 (Anthropic ANZ live walk, 2026-07-05 — "pretty
          print issues"): descriptionBlocks (formatJobDescription,
          jobDescriptionFormat.ts) replaces the old single pre-wrap <div> with
          real <p>/<ul><li> elements, recognizing ONLY structure the served
          text itself already carries (blank-line paragraph boundaries,
          "- "/bullet-marker runs, a conservative colon-anchored heading
          line) — see that module's own header for the full no-invention
          contract. The root cause of the run-together render was upstream
          (JobExtractionService.ts's stripHtml used to flatten every HTML tag
          to a single space); THAT is fixed at the data path, so a freshly-
          extracted description now carries the newlines this formatter
          reads. A description extracted before that fix landed (or a thin
          paste with no blank lines at all) still degrades honestly to one
          paragraph — never a fabricated structure. */}
      <Card className={styles.sectionCard}>
        <details
          open={collapseState['about-this-role']}
          onToggle={(e) => handleToggleCollapse('about-this-role', e.currentTarget.open)}
        >
          <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
            About this role
          </summary>
          {descriptionBlocks.length > 0 ? (
            <div className={styles.jobDescriptionBody}>
              {descriptionBlocks.map((block, i) => {
                if (block.kind === 'bullets') {
                  return (
                    <ul key={i} className={styles.jobDescriptionList}>
                      {block.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  );
                }
                if (block.kind === 'heading') {
                  return (
                    <p key={i} className={styles.jobDescriptionHeading}>
                      {block.text}
                    </p>
                  );
                }
                return <p key={i}>{block.text}</p>;
              })}
            </div>
          ) : (
            <div className={styles.matchNote} style={{ marginTop: 8 }}>
              No description was captured for this listing — I only have what's on this card to go
              on.
            </div>
          )}
        </details>
      </Card>

      {/* FIX 1 (2026-07-05, sharpened priority): "What I'll add" / "Lead
          achievements I put forward" is one of the informational cards named
          explicitly in the collapse-everything push — now CLOSED by default
          (collapsibleSection.ts's 'what-ill-add'), reusing the SAME
          <details>/<summary className={styles.sectionTitle}> idiom "Why
          this fit"/"About this role" already establish. Both the done and
          not-done branches share ONE section id (they're mutually exclusive
          renders of the same conceptual card slot — a user who expands this
          before tailoring and then tailors sees the SAME section stay
          expanded, not silently reset to closed). */}
      {done ? (
        <Card className={styles.sectionCard}>
          <details
            open={collapseState['what-ill-add']}
            onToggle={(e) => handleToggleCollapse('what-ill-add', e.currentTarget.open)}
          >
            <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
              Lead achievements I put forward
            </summary>
            {tailorResult.tailoring.cvBrief.leadAchievements.length === 0 ? (
              <div className={styles.matchNote} style={{ marginTop: 8 }}>
                Nothing traced cleanly enough from your story to lead with for this role.
              </div>
            ) : (
              <div className={styles.bulletList} style={{ marginTop: 8 }}>
                {tailorResult.tailoring.cvBrief.leadAchievements.map((text) => (
                  <div key={text} className={styles.bulletRow}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      style={{ marginTop: 1, flexShrink: 0 }}
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="var(--tl-good)"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className={styles.bulletText}>{text}</div>
                  </div>
                ))}
              </div>
            )}
            {/* AXIS-P3 (Package D) + F2 (docs/briefs/morning-2026-07-07/
                06-feature-opportunities.md, WP-C) — the lens-off/lens-on
                comparison, now with the spider-chart visual Jason asked for
                ("this needs to have visual representation like the current
                spider web graph that we use"). Renders ONLY when
                lensRanking.applied === true; absent or false -> literally
                no new DOM (additive/never-fabricate contract unchanged). */}
            {tailorResult.tailoring.lensRanking?.applied === true ? (
              <div className={styles.lensComparison}>
                <div className={styles.lensComparisonLabel}>How the axis lens reordered this</div>
                <LensComparisonRadar
                  modelOrder={tailorResult.tailoring.lensRanking.modelOrder}
                  lensOrder={tailorResult.tailoring.lensRanking.lensOrder}
                />
                <details className={styles.lensComparisonDetails}>
                  <summary className={styles.lensComparisonDetailsSummary}>
                    See the two orderings as text
                  </summary>
                  <div className={styles.lensComparisonColumns}>
                    <div className={styles.lensComparisonColumn}>
                      <div className={styles.lensComparisonColumnTitle}>Default order</div>
                      {tailorResult.tailoring.lensRanking.modelOrder.map((text) => (
                        <div key={text} className={styles.lensComparisonItem}>
                          {text}
                        </div>
                      ))}
                    </div>
                    <div className={styles.lensComparisonColumn}>
                      <div className={styles.lensComparisonColumnTitle}>Lens-ranked order</div>
                      {tailorResult.tailoring.lensRanking.lensOrder.map((text) => (
                        <div key={text} className={styles.lensComparisonItem}>
                          {text}
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              </div>
            ) : null}
          </details>
        </Card>
      ) : (
        <Card className={styles.sectionCard}>
          <details
            open={collapseState['what-ill-add']}
            onToggle={(e) => handleToggleCollapse('what-ill-add', e.currentTarget.open)}
          >
            <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
              What I'll add
            </summary>
            <div className={styles.matchNote} style={{ marginTop: 8 }}>
              Tailor this role to see the real achievements from your story that best fit it — nothing to
              show until then.
            </div>
          </details>
        </Card>
      )}

      {/* FIX 1 (2026-07-05, sharpened priority): "How I framed the gaps" —
          same collapse-by-default treatment, same reused idiom. */}
      {done && tailorResult.tailoring.cvBrief.gapTranslations.length > 0 ? (
        <Card className={styles.sectionCardLast}>
          <details
            open={collapseState['how-i-framed-gaps']}
            onToggle={(e) => handleToggleCollapse('how-i-framed-gaps', e.currentTarget.open)}
          >
            <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
              How I framed the gaps
            </summary>
            <div className={styles.bulletList} style={{ marginTop: 8 }}>
              {tailorResult.tailoring.cvBrief.gapTranslations.map((g) => (
                <div key={g.gap} className={styles.bulletRow}>
                  <div className={`${styles.bulletDot} ${styles.bulletDotAdd}`}>+</div>
                  <div className={styles.bulletText}>
                    <strong>{g.gap}:</strong> {g.translation}
                  </div>
                </div>
              ))}
            </div>
          </details>
        </Card>
      ) : null}

      {/* THE PRE-SHAPING WALK (ruling #3 — "There also should be a would you
          like to add anything beforehand"). Shown once per job, per tab
          session, ONLY while at least one gap is unaddressed and only
          before the shaping action (tailorPhase === 'idle') — dismissible,
          never a nag (preShapingPrompt.ts remembers the dismissal). Offers
          EITHER a jump straight into the next unaddressed gap's
          conversation OR a plain "no thanks, tailor as-is" dismissal. */}
      {tailorPhase === 'idle' && showPreShaping && gapsResult?.nextUnaddressed ? (
        <Card className={styles.sectionCard}>
          <div className={styles.matchLabel}>Would you like to add anything beforehand?</div>
          <div className={styles.matchNote} style={{ marginTop: 6 }}>
            Before I shape your resume and cover letter — there's still a gap open: "
            {gapsResult.nextUnaddressed.text}". Want to address it first?
          </div>
          <div className={styles.doneActions} style={{ marginTop: 10 }}>
            <button
              type="button"
              className={styles.previewBtn}
              onClick={() => {
                if (gapsResult.nextUnaddressed) setOpenGap(gapsResult.nextUnaddressed);
                setShowPreShaping(false);
                if (jobId) dismissPreShaping(jobId);
              }}
            >
              Address it now
            </button>
            <button
              type="button"
              className={styles.copyLetterBtn}
              onClick={() => {
                setShowPreShaping(false);
                if (jobId) dismissPreShaping(jobId);
              }}
            >
              No thanks, shape it as-is
            </button>
          </div>
        </Card>
      ) : null}

      {jobId && openGap ? (
        <GapAddressPanel
          jobId={jobId}
          gap={openGap}
          onClose={() => setOpenGap(null)}
          onAddressed={(result) => {
            if (jobId) handleGapAddressed(jobId, result);
          }}
        />
      ) : null}

      {tailorPhase === 'idle' && (
        <button type="button" className={styles.tailorButton} onClick={handleTailor}>
          Tailor my resume & cover letter
        </button>
      )}
      {tailorPhase === 'tailoring' && (
        <div className={styles.tailoringRow}>
          <span className={styles.spinner} />
          Weaving in your threads…
        </div>
      )}
      {tailorPhase === 'error' && tailorError ? (
        <div className={styles.doneWrap}>
          <div className={styles.tailorErrorRow} role="alert">
            {tailorError}
          </div>
          <button type="button" className={styles.tailorButton} onClick={handleTailor}>
            Try again
          </button>
        </div>
      ) : null}

      {tailorPhase === 'entitlement_blocked' && entitlementBlock ? (
        <div className={styles.doneWrap}>
          <EntitlementBlockedNote block={entitlementBlock} />
        </div>
      ) : null}

      {done && (
        <div className={styles.doneWrap}>
          {blocked && tailorResult.lintGate ? (
            <LintGateBlockedNote lintGate={tailorResult.lintGate} />
          ) : (
            <div className={styles.doneRow}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Resume & cover letter tailored
            </div>
          )}

          {advisoryLintGate ? <AdvisoryLintNote lintGate={advisoryLintGate} /> : null}

          {notFullyGrounded && groundingFlags.length > 0 ? (
            <div className={styles.groundingFlagBox} role="status">
              <div className={styles.groundingFlagTitle}>Flagged for review before you send</div>
              {groundingFlags.map((flag, i) => (
                <div key={`${flag.slot}-${i}`} className={styles.groundingFlagLine}>
                  {flag.reason === 'entity_mismatch'
                    ? `A number ("${flag.value}") in the ${slotLabel(flag.slot)} looks real, but not at ${flag.entity ?? 'the company named there'} — take a look before you send.`
                    : flag.reason === 'ungrounded_figure'
                      ? `A number ("${flag.value}") in the ${slotLabel(flag.slot)} didn't clearly match anything you've told me — take a look before you send.`
                      : `A line in the ${slotLabel(flag.slot)} didn't trace cleanly to your story: "${flag.value}"`}
                </div>
              ))}
            </div>
          ) : null}

          <div className={styles.coverLetterPreview}>
            <div className={styles.coverLetterHeader}>
              <div className={styles.sectionTitle}>Cover letter — ready to send</div>
              <div className={styles.coverLetterActions}>
                <button type="button" className={styles.copyLetterBtn} onClick={handleCopyLetter}>
                  {copyPhase === 'copied' ? 'Copied' : copyPhase === 'error' ? "Couldn't copy" : 'Copy'}
                </button>
                <button type="button" className={styles.copyLetterBtn} onClick={handlePrintLetter}>
                  Print / Save as PDF
                </button>
                {tailorResult?.coverLetterId ? (
                  <DriveSaveButton artifactType="cover_letter" artifactId={tailorResult.coverLetterId} />
                ) : null}
              </div>
            </div>
            {printLetterNote ? (
              <div className={styles.toolbarNote}>{printLetterNote}</div>
            ) : null}
            <div className={styles.coverLetterBody}>{tailorResult.tailoring.coverLetter}</div>
            {/* HONESTY-REVIEWER (R332 swap; the smoke-fire's readable verdict —
                the panel was served but never rendered until 2026-07-08, the
                landed-but-strangled class again). Advisory only, never blocks
                the letter above; every field VERBATIM from the server. */}
            {tailorResult.tailoring.bullshitReview ? (
              <div className={styles.readersChairBox} role="status">
                <div className={styles.readersChairVerdict}>
                  {tailorResult.tailoring.bullshitReview.revised
                    ? 'Reader\u2019s chair, after one rewrite: '
                    : 'Reader\u2019s chair: '}
                  {tailorResult.tailoring.bullshitReview.overallVerdict}
                </div>
                {tailorResult.tailoring.bullshitReview.findings.map((finding, i) => (
                  <div key={i} className={styles.readersChairFinding}>
                    <span className={styles.readersChairChip}>{describeBullshitCategory(finding.category)}</span>
                    <div className={styles.readersChairQuote}>&ldquo;{finding.quote}&rdquo;</div>
                    <div className={styles.readersChairReaction}>{finding.reaction}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {trackPhase === 'tracked' ? (
            <div className={styles.trackedRow} role="status">
              Tracked — see it on the Track tab.
            </div>
          ) : (
            <div className={styles.doneActions}>
              <button
                type="button"
                className={styles.previewBtn}
                disabled={trackPhase === 'tracking'}
                onClick={handleTrack}
              >
                {trackPhase === 'tracking' ? 'Tracking…' : 'Track this application'}
              </button>
            </div>
          )}
          {trackPhase === 'error' && trackError ? (
            <div className={styles.tailorErrorRow} role="alert">
              {trackError}
            </div>
          ) : null}
        </div>
      )}

      {/* GAP 1 — the tailored-résumé DOCUMENT for this job (distinct from the
          cover letter above). POST /jobs/:id/resume is a deterministic
          composer, not entitlement-gated, and works whether or not a tailor
          has run yet (an untailored build falls back to the corpus's own
          ordering — never blocked on this session's tailorPhase). Always
          available once the job itself has loaded, same honest-default
          scenario resolution the Tailor button above already relies on
          (omitting scenarioId lets the server resolve primary → first
          active, exactly like tailorJob(jobId) does with no explicit id).
          roleTitle/company/applicantName are passed through ONLY to name the
          print popup (the house file-naming convention) — never rendered
          into the résumé document itself. */}
      {jobId ? (
        <JobResumePanel
          jobId={jobId}
          roleTitle={job.title}
          company={job.company}
          applicantName={auth.user?.displayName}
        />
      ) : null}

      {/* THE ALIGNMENT ESSAY — docs/design/alignment-essay-module-2026-07-05.
          md. Sits below the résumé panel (per the brief's placement
          recommendation: "JobDetail beside the tailor outputs"). Always
          available once the job itself has loaded — not gated on
          tailorPhase, mirroring JobResumePanel's own posture.
          jobDescription/jobRequirements pass down job.description/
          job.requirements — the SAME fields this page already renders
          verbatim in its own "About this role" card above (lines ~494-518)
          — so the panel's word-guidance scan (the owner's live-test finding:
          the application page itself often states the winning answer
          length) has real text to search WITHOUT a second fetch. */}
      {jobId ? (
        <AlignmentEssayPanel
          jobId={jobId}
          company={job.company}
          roleTitle={job.title}
          applicantName={auth.user?.displayName}
          jobDescription={job.description}
          jobRequirements={job.requirements}
          essayCorrections={essayCorrections}
          onEssayCorrected={(c) =>
            setEssayCorrections((prev) => ({
              ...prev,
              [claimCorrectionKey(jobId, c.flaggedText)]: c,
            }))
          }
        />
      ) : null}
    </div>
  );
}

/** Honest phrasing for the two ApiError shapes this route can actually
 *  return (503 no_anthropic_key, 400 no_active_scenario) plus the generic
 *  fallback — never a made-up "something went wrong" that hides which of
 *  the two real causes actually happened. */
function describeTailorError(err: unknown): string {
  const apiErr = err as ApiError;
  const body = apiErr?.body as { error?: string } | undefined;
  if (body?.error === 'no_anthropic_key') {
    return "Tailoring isn't ready yet on my end — try again in a bit.";
  }
  if (body?.error === 'no_active_scenario') {
    return "I need to know what you're job-hunting for before I can tailor this — set that up first.";
  }
  return err instanceof Error ? err.message : 'Could not tailor this role — try again.';
}

/** LANE JOB-TRUST — the honest word for a per-scenario chip whose fitScore is
 *  null, naming WHY rather than rendering a bare dash the user has to
 *  interpret. Mirrors the route's own fitDetermination vocabulary exactly
 *  (jobs.routes.ts's GET /jobs/:id: 'undetermined' | 'unscored' | 'scored') —
 *  a chip only reaches this function when fitScore is null, so 'scored' is
 *  unreachable in practice, but it's covered anyway rather than left to fall
 *  through to a made-up default. */
function scenarioScoreLabel(determination: JobScoreRecord['fitDetermination']): string {
  switch (determination) {
    case 'undetermined':
      return 'undetermined';
    case 'unscored':
      return 'unscored';
    default:
      return '—';
  }
}

/** G1 (docs/CHANGE-CONTROL.md R184) — the honest, SPECIFIC line for each real
 *  ResolvedEntitlement.reason the server can serve on a 402 (mirrors
 *  entitlements.ts's own reason vocabulary exactly — this is the FULL set,
 *  never a partial one that silently falls through to a generic default for
 *  a reason this file forgot to add). No-frontend-bandaids: this DESCRIBES
 *  the served reason in plain words, it never invents a DIFFERENT reason. */
function describeEntitlementReason(reason: string): string {
  switch (reason) {
    case 'trial_expired':
      return "Your free trial's wrapped up.";
    case 'past_due_lapsed':
      return 'Your last payment didn’t go through, and the grace period’s run out too.';
    case 'canceled':
      return 'Your subscription’s been canceled.';
    case 'no_subscription':
      return "Tailoring's a paid feature — want to start a trial?";
    default:
      return "Tailoring's a paid feature — want to start a trial?";
  }
}

/** G1 — the tailoring 402's render. Reuses the SAME advisory-note idiom
 *  AdvisoryLintNote already establishes below (styles.advisoryBox/
 *  advisoryTitle, role="status" — a calm, non-alarming surface, not
 *  styles.tailorErrorRow's role="alert" red-flag treatment: this is an
 *  expected, honest gate, not a failure). Renders the SERVED tier/reason/hint
 *  verbatim (via describeEntitlementReason, never a client-invented message)
 *  and links to Settings (where GET /billing/status + POST /billing/checkout
 *  already live) — a REAL, working action, never a dead button. */
function EntitlementBlockedNote({ block }: { block: { tier: string; reason: string; hint: string } }) {
  return (
    <div className={styles.advisoryBox} role="status">
      <div className={styles.advisoryTitle}>{describeEntitlementReason(block.reason)}</div>
      <div className={styles.matchNote}>
        {block.hint === 'start your trial'
          ? "Start your free trial to tailor this role — I'll weave in your real story."
          : block.hint}
      </div>
      <Link to="/settings" className={styles.tailorButton} style={{ display: 'inline-block', marginTop: 10 }}>
        Go to billing
      </Link>
    </div>
  );
}

function slotLabel(slot: 'coverLetterFigure' | 'gapTranslation'): string {
  return slot === 'coverLetterFigure' ? 'cover letter' : 'gap framing';
}

/** Renders the render-BLOCKING lint gate's own per-block diffs verbatim —
 *  the brief's "gates honestly (blocked/lint states)" law. Every diff shown
 *  here is a REAL flagged span + reason + suggestion the server computed
 *  (LintGateService.ts); this never collapses the gate into a plain "failed"
 *  message. */
function LintGateBlockedNote({ lintGate }: { lintGate: NonNullable<TailorResponse['lintGate']> }) {
  const failing = lintGate.blocks.filter((b) => !b.passed);
  return (
    <div className={styles.blockedBox} role="alert">
      <div className={styles.blockedTitle}>
        Held back — {lintGate.blockingCount} bit{lintGate.blockingCount === 1 ? '' : 's'} needed another
        pass before I'd send them
      </div>
      {failing.map((block) => (
        <div key={block.location} className={styles.blockedBlock}>
          <div className={styles.blockedLocation}>{block.location}</div>
          {block.diffs.map((diff, i) => (
            <div key={i} className={styles.blockedDiff}>
              {diff.span ? <span className={styles.blockedSpan}>"{diff.span}"</span> : null}
              <span className={styles.blockedSuggestion}> — {diff.suggestion}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** M7 — the ADVISORY twin of LintGateBlockedNote: renders the SAME real
 *  per-block diffs (never summarized), but only ever reached when the tailor
 *  route did NOT set blocked:true (a brevity-only failure — see
 *  TailorLintSeverity.ts / advisoryLintGate above). Framed as suggestions
 *  alongside the "Resume & cover letter tailored" row, not instead of it —
 *  the brief's "tailoring is critical to the voice of the user" law means a
 *  brevity nit is worth showing, never worth holding the render back for. */
function AdvisoryLintNote({ lintGate }: { lintGate: NonNullable<TailorResponse['lintGate']> }) {
  const failing = lintGate.blocks.filter((b) => !b.passed);
  return (
    <div className={styles.advisoryBox} role="status">
      <div className={styles.advisoryTitle}>
        Brevity suggestions — {failing.length} block{failing.length === 1 ? '' : 's'} could tighten
        further (nothing held back)
      </div>
      {failing.map((block) => (
        <div key={block.location} className={styles.blockedBlock}>
          <div className={styles.blockedLocation}>{block.location}</div>
          {block.diffs.map((diff, i) => (
            <div key={i} className={styles.blockedDiff}>
              {diff.span ? <span className={styles.blockedSpan}>"{diff.span}"</span> : null}
              <span className={styles.blockedSuggestion}> — {diff.suggestion}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
